import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useMemo } from 'react';
import { endOfYear, format, isSameMonth, startOfQuarter, startOfYear } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ConditionalWrapper, Spinner, Typography, WithInfoIcon } from 'seagull';
import GenericError from '~/components/GenericError/GenericError';
import { DateRange } from '~/components/TableFilters/types';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { RevenueStatus, HistoricRevenueModel } from '~/models/HistoricRevenueModel';
import { toDatestampArray } from '~/models/time';
import { selectors } from '~/store/state-selectors';
import { formatNumericUSDate, utils } from '~/utils';
import { getLastXMonths } from '~/utils/date.utils';
import { commonBarChartProps, commonBarProps, commonCartesianGridProps, commonXAxisProps } from '../charts/commonChartProps';
import CustomBarShape from '../charts/CustomBarShape/CustomBarShape';
import CustomLegend from '../charts/CustomLegend/CustomLegend';
import CustomTooltip from '../charts/CustomTooltip/CustomTooltip';
import CustomXAxisTick from '../charts/CustomXAxisTick/CustomXAxisTick';
import GraphWidgetBottomCard from '../GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '../GraphWidget/GraphWidgetLayout';
import GraphWidgetTopCard from '../GraphWidget/GraphWidgetTopCard';
import { usePayoutsSummary } from '../PayoutsSummaryWidget/hooks/usePayoutsSummary';
import { useHistoricRevenueStats } from './hooks/useHistoricRevenueStats';
import styles from './RevenueWidget.module.scss';
import { colorsMap, getLegendItems, labelMap, revenueStatusOrder } from './RevenueWidget.utils';
const revenueWidgetTextsPath = 'pages.dashboard.revenueWidget';
const stackId = 'a';
const topCards = (t, historicRevenueStats, loading)=>compact([
        /*#__PURE__*/ _jsx(TopCard, {
            testId: "revenue-stats-earned-this-year",
            title: t.earnedThisYear.title(),
            titleInfo: t.earnedThisYear.tooltip(),
            primaryText: historicRevenueStats.totalThisYear(),
            linkTo: "".concat(routes.revenueReport, "?dateRange=").concat(formatNumericUSDate(startOfYear(new Date())), ",").concat(formatNumericUSDate(endOfYear(new Date())), "&dateRangeOption=").concat(DateRange.CurrentYear),
            loading: loading
        }, "earned-this-year"),
        /*#__PURE__*/ _jsx(TopCard, {
            testId: "revenue-stats-expected-this-year",
            title: t.expectedThisYear.title(),
            titleInfo: t.expectedThisYear.tooltip(),
            primaryText: historicRevenueStats.totalExpectedThisYear(),
            linkTo: routes.futureInvoicesReport,
            loading: loading
        }, "expected-this-year")
    ]);
export function getXAxisText(props) {
    const date = props.payload.value;
    return isSameMonth(startOfQuarter(date), date) ? format(date, 'MMM') : '';
}
function getTooltipItems(payload) {
    if (!payload || !payload.length) return;
    const historicRevenueModel = payload[0].payload;
    return revenueStatusOrder.filter((status)=>historicRevenueModel[status].totalAmount.gt(0)).map((status)=>({
            label: "".concat(utils.formatDollarAmount(historicRevenueModel[status].totalAmount), " ").concat(labelMap[status]),
            color: colorsMap[status]
        }));
}
function getTitle(_label, payload) {
    if (!payload || !payload.length) return '';
    const historicRevenueModel = payload[0].payload;
    return format(historicRevenueModel.date, 'MMMM');
}
function HistoricRevenueChart(param) {
    let { data, testId, isTestMode } = param;
    const barChartProps = isTestMode ? {
        height: 100,
        width: 100
    } : {};
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testId,
        className: styles.chart,
        children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
            condition: !isTestMode,
            wrapper: (children)=>/*#__PURE__*/ _jsx(ResponsiveContainer, {
                    minHeight: 300,
                    children: children
                }),
            children: /*#__PURE__*/ _jsxs(BarChart, _object_spread_props(_object_spread(_object_spread_props(_object_spread({}, commonBarChartProps), {
                data: data,
                barCategoryGap: '10%'
            }), barChartProps), {
                margin: {
                    top: 20,
                    left: 16,
                    right: 16
                },
                children: [
                    /*#__PURE__*/ _jsx(CartesianGrid, _object_spread({}, commonCartesianGridProps)),
                    /*#__PURE__*/ _jsx(Legend, {
                        content: /*#__PURE__*/ _jsx(CustomLegend, {
                            testId: "".concat(testId, "-legend"),
                            items: getLegendItems(data)
                        })
                    }),
                    /*#__PURE__*/ _jsx(XAxis, _object_spread_props(_object_spread({}, commonXAxisProps), {
                        dataKey: (item)=>item.date,
                        height: 49,
                        tick: /*#__PURE__*/ _jsx(CustomXAxisTick, {
                            tickHeight: 30,
                            getText: getXAxisText
                        })
                    })),
                    /*#__PURE__*/ _jsx(YAxis, {
                        width: 70,
                        axisLine: false,
                        tickLine: false,
                        fontSize: 12,
                        tickFormatter: (value)=>value === 0 ? '' : utils.formatDollarAmount(value)
                    }),
                    /*#__PURE__*/ _jsx(Tooltip, {
                        cursor: false,
                        isAnimationActive: false,
                        content: /*#__PURE__*/ _jsx(CustomTooltip, {
                            skin: "dark",
                            testId: "".concat(testId, "-tooltip"),
                            getTitle: getTitle,
                            getItems: getTooltipItems
                        })
                    }),
                    revenueStatusOrder.map((status)=>/*#__PURE__*/ _createElement(Bar, _object_spread_props(_object_spread({}, commonBarProps), {
                            key: status,
                            dataKey: (item)=>item[status].totalAmount.toNumber(),
                            stackId: stackId,
                            fill: colorsMap[status],
                            shape: /*#__PURE__*/ _jsx(CustomBarShape, {
                                getHasRadius: (item)=>{
                                    const statusIndex = revenueStatusOrder.indexOf(status);
                                    const statusesAbove = revenueStatusOrder.slice(statusIndex + 1);
                                    return !statusesAbove.some((s)=>item[s].totalAmount.gt(0));
                                }
                            })
                        })))
                ]
            }))
        })
    });
}
const PlaceholderChart = (props)=>{
    const { getNow } = useIoContext();
    const placeholderData = getLastXMonths(getNow(), 12).map((date)=>({
            date,
            [RevenueStatus.Paid]: {
                date: toDatestampArray(date),
                totalAmount: new Decimal(4000)
            },
            [RevenueStatus.Expected]: {
                date: toDatestampArray(date),
                totalAmount: new Decimal(1000)
            },
            [RevenueStatus.PaidExternally]: {
                date: toDatestampArray(date),
                totalAmount: new Decimal(4000)
            }
        }));
    return /*#__PURE__*/ _jsx(HistoricRevenueChart, _object_spread({
        testId: "revenue-chart-placeholder",
        data: placeholderData
    }, props));
};
const TopCard = (param)=>{
    let { title, testId, linkTo, success, loading, titleInfo = '', primaryText } = param;
    return /*#__PURE__*/ _jsx(GraphWidgetTopCard, {
        linkTo: linkTo,
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": loading ? "".concat(testId, "-loading") : testId,
            className: styles.topCard,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsx(WithInfoIcon, {
                        showInfo: !!titleInfo,
                        info: titleInfo,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            skin: success ? 'success' : 'standard',
                            variant: "paragraph1",
                            ellipsis: true,
                            children: title
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    weight: "normal",
                    variant: "h5",
                    testId: testId && "".concat(testId, "-primary"),
                    children: loading ? /*#__PURE__*/ _jsx(Spinner, {
                        size: 30
                    }) : primaryText
                })
            ]
        })
    });
};
const RevenueWidgetContent = (props)=>{
    const { loading, historicRevenueStats, payouts, isTestMode, loadingOrEmptyBg } = props;
    const { t } = useLocaleKeys(revenueWidgetTextsPath);
    const data = useMemo(()=>historicRevenueStats.chartData(), [
        historicRevenueStats
    ]);
    return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
        title: t.title(),
        loading: loading,
        bottomHeight: "l",
        children: [
            topCards(t, historicRevenueStats, loading),
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                empty: !payouts.hasEverHadPaidPayout() && !historicRevenueStats.hasRevenueThisYear() && !historicRevenueStats.hasExpectedRevenue(),
                emptyMsg: t.emptyMsg(),
                testId: "revenue-stats",
                loadingOrEmptyBg: loadingOrEmptyBg,
                children: /*#__PURE__*/ _jsx(HistoricRevenueChart, {
                    testId: "revenue-stats-chart",
                    data: data,
                    isTestMode: isTestMode
                })
            })
        ]
    });
};
const RevenueWidget = (param)=>{
    let { isTestMode } = param;
    const { historicRevenueStats, isLoading: revenueLoading, isFetching: revenueFetching, error: revenueError } = useHistoricRevenueStats({
        refreshOnHookMount: true
    });
    const { payoutsSummary, isLoading: payoutsLoading, isFetching: payoutsFetching, error: payoutsError } = usePayoutsSummary({
        refreshOnHookMount: true
    });
    const isMemberUser = useStore(selectors.user.isMemberUser);
    const { getNow } = useIoContext();
    const { t } = useLocaleKeys(revenueWidgetTextsPath);
    const now = useMemo(()=>getNow(), []);
    const loadingOrEmptyBg = ()=>/*#__PURE__*/ _jsx(PlaceholderChart, {
            isTestMode: isTestMode
        });
    const isLoading = revenueLoading || payoutsLoading;
    const isFetching = revenueFetching || payoutsFetching;
    const error = revenueError || payoutsError;
    if (isMemberUser) return null;
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: "widget-error"
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
            title: t.title(),
            loading: true,
            bottomHeight: "l",
            children: [
                topCards(t, new HistoricRevenueModel([], [], [], new Decimal(0), now), true),
                /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                    loading: true,
                    loadingMsg: t.loadingMsg(),
                    loadingOrEmptyBg: loadingOrEmptyBg
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(RevenueWidgetContent, {
        loading: isFetching,
        payouts: payoutsSummary,
        isTestMode: isTestMode,
        historicRevenueStats: historicRevenueStats,
        loadingOrEmptyBg: loadingOrEmptyBg
    });
};
export default RevenueWidget;
